import { useEffect, useRef, useState } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { POSModel } from "../../models/orders/order-list-model";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";

interface RawMaterialMovementSearchProps {
  setLoading: (loading: boolean) => void;
  onSearch: (data: any[]) => void;
  onSummary: (data: any[]) => void;
  onReset?: () => void;
  onKeywordChange: (keyword: string) => void;
}

const movementTypeOptions = [
  { label: "Inward", value: 1 },
  { label: "Outward", value: 2 },
  { label: "Adjust", value: 3 },
];

// Alias for manufacturing unit label shown in dropdown
const aliasMuLabel = (row: any): string => {
  const code = String(row?.posCode ?? "").toUpperCase();
  const name = String(row?.posName ?? row?.name ?? "").toUpperCase();
  const addr2 = String(row?.address2 ?? row?.Address2 ?? "").toUpperCase();

  if (code.includes("POS-003") || code.includes("POS003")) return "Unit-1-KR";
  if (code.includes("POS-004") || code.includes("POS004")) return "Unit-2-MT";

  if (name.includes("K R NAGAR") || addr2.includes("K R NAGAR") || addr2.includes("KR NAGAR"))
    return "Unit-1-KR";
  if (name.includes("MAGADI") || addr2.includes("MAGADI")) return "Unit-2-MT";

  return row?.posCode ?? row?.posName ?? `Unit-${row?.posID ?? ""}`;
};

const RawMaterialMovementSearch: React.FC<RawMaterialMovementSearchProps> = ({
  setLoading,
  onSearch,
  onSummary,
  onReset,
  onKeywordChange,
}) => {
  const toast = useRef<Toast>(null);

  const [posList, setPosList] = useState<POSModel[]>([]);
  const [selectedPosId, setSelectedPosId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [movementTypes, setMovementTypes] = useState<number[]>([1, 2, 3]);
  const [keyword, setKeyword] = useState<string>("");

  useEffect(() => {
    const fetchPOS = async () => {
      try {
        const response = await RawMaterialService.GetManufacrturingunitlist();
        setPosList(response || []);
        if (Array.isArray(response) && response.length === 1) {
          const singlePOSId = response[0].posID;
          setSelectedPosId(singlePOSId);
        }
      } catch {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load Manufacturing Unit data.",
        });
      }
    };
    fetchPOS();
  }, []);

  const posOptions = (posList ?? []).map((p: any) => ({
    ...p,
    label: aliasMuLabel(p),
    value: p.posID,
  }));

  const handleSearch = async () => {
    if (!selectedPosId) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the Manufacturing Unit",
      });
      return;
    }

    if (!startDate && !endDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the Start date and End date",
      });
      return;
    }

    if (!startDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the Start date",
      });
      return;
    }

    if (!endDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select the End date",
      });
      return;
    }

    if (movementTypes.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Please select at least one movement type",
      });
      return;
    }

    setLoading(true);

    const adjustedStartDate = new Date(startDate);
    adjustedStartDate.setHours(0, 0, 0, 0);

    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setHours(23, 59, 59, 999);

    const formatToSQLDateTime = (date: Date) => date.toLocaleString("sv-SE").replace(" ", "T");

    const payload = {
      posId: selectedPosId,
      startDate: formatToSQLDateTime(adjustedStartDate),
      endDate: formatToSQLDateTime(adjustedEndDate),
      movementTypes: movementTypes,
    };

    try {
      const result = await RawMaterialService.GetRawMaterialMovementLog(payload);
      console.log("Raw material movement API response:", result);
      const movementResult =
        result?.movemementresult ??
        result?.movemementResult ??
        result?.Movemementresult ??
        result?.MovemementResult ??
        result?.movementresult ??
        result?.movementResult ??
        result?.Movementresult ??
        result?.MovementResult;

      if (Array.isArray(movementResult) && movementResult.length > 0) {
        onSearch(movementResult);
      } else {
        toast.current?.show({
          severity: "info",
          summary: "No data",
          detail: "No movement records found.",
        });
        onSearch([]);
      }

      const Result1 = await RawMaterialService.GetRawMaterialStockSummary(payload);
      if (Result1?.summaryresult) {
        onSummary(Result1.summaryresult);
      } else {
        onSummary([]);
      }
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch movement logs or summary.",
      });
      onSearch([]);
      onSummary([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (posList.length === 1) {
      setSelectedPosId(posList[0].posID);
    } else {
      setSelectedPosId(null);
    }
    setStartDate(null);
    setEndDate(null);
    setMovementTypes([1, 2, 3]);
    setKeyword("");
    onKeywordChange("");
    onSearch([]);
    onSummary([]);

    if (onReset) {
      onReset();
    }
  };

  return (
    <div className="card p-4 w-full" style={{
      backgroundColor: '#e8f5e9',
      borderRadius: '12px'
    }}>
      <Toast ref={toast} />

      {/* Header Section */}
      <div className="flex align-items-center gap-3 mb-4" style={{
        backgroundImage: 'linear-gradient(to right, white, rgb(232, 245, 233))',
        padding: '16px',
        borderRadius: '8px',
        marginLeft: '-16px',
        marginRight: '-16px',
        marginTop: '-16px',
        paddingLeft: '16px',
        paddingRight: '16px'
      }}>
        <div style={{
          backgroundColor: 'rgb(76, 175, 80)',
          borderRadius: '10px',
          padding: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <i className="pi pi-sync" style={{ color: 'white', fontSize: '1.5rem' }}></i>
        </div>
        <div>
          <h5 className="m-0 mb-1" style={{ color: '#2d3748', fontWeight: '600' }}>
            Raw Material Movement
          </h5>
          <p className="m-0" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Track, manage, and view raw material inventory movements
          </p>
        </div>
      </div>

      {/* Filter Header */}
      <div className="flex align-items-center gap-2 mb-3">
        <i className="pi pi-filter" style={{ color: 'rgb(76, 175, 80)', fontSize: '1.1rem' }}></i>
        <span style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.95rem' }}>Filter Movements</span>
      </div>

      <div className="grid">
        {/* Manufacturing Unit Dropdown */}
        <div className="col-12 md:col-2">
          <Dropdown
            value={selectedPosId}
            options={posOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Manufacturing Unit"
            onChange={(e: DropdownChangeEvent) => setSelectedPosId(e.value)}
            className="w-full custom-rounded-dropdown"
            disabled={posOptions.length === 1}
            pt={{
              root: { style: { borderRadius: '8px' } },
              input: { style: { borderRadius: '8px' } }
            }}
          />
        </div>

        {/* Start Date */}
        <div className="col-6 md:col-2">
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate(e.value as Date | null)}
            showButtonBar
            dateFormat="dd/mm/yy"
            placeholder="Start Date"
            className="w-full"
            readOnlyInput
            inputStyle={{ borderRadius: '8px' }}
          />
        </div>

        {/* End Date */}
        <div className="col-6 md:col-2">
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate(e.value as Date | null)}
            showButtonBar
            dateFormat="dd/mm/yy"
            placeholder="End Date"
            className="w-full"
            readOnlyInput
            inputStyle={{ borderRadius: '8px' }}
          />
        </div>

        {/* Movement Types */}
        <div className="col-6 md:col-2">
          <MultiSelect
            value={movementTypes}
            options={movementTypeOptions}
            optionLabel="label"
            optionValue="value"
            placeholder="Movement Types"
            className="w-full"
            onChange={(e) => setMovementTypes(e.value)}
            display="chip"
            pt={{
              root: { style: { borderRadius: '8px' } }
            }}
          />
        </div>

        {/* Keyword Search */}
        <div className="col-6 md:col-2">
          <span className="p-input-icon-left w-full">
            <i className="pi pi-search" />
            <InputText
              type="search"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                onKeywordChange(e.target.value);
              }}
              placeholder="Search..."
              className="w-full"
              style={{ borderRadius: '8px' }}
            />
          </span>
        </div>

        {/* Filter Button */}
        <div className="col-6 md:col-1">
          <Button
            label="Filter"
            onClick={handleSearch}
            severity="success"
            className="w-full"
            style={{ borderRadius: '8px' }}
          />
        </div>

        {/* Reset Button */}
        <div className="col-6 md:col-1">
          <Button
            label="Reset"
            onClick={handleReset}
            severity="secondary"
            className="w-full"
            text
            raised
            style={{ borderRadius: '8px' }}
          />
        </div>
      </div>
    </div>
  );
};

export default RawMaterialMovementSearch;
